import { Inject, Injectable } from '@angular/core';
import { Plugins, registerWebPlugin } from '@capacitor/core';
import { OAuth2AuthenticateOptions, OAuth2Client } from '@byteowls/capacitor-oauth2';
import { User } from 'firebase';
import * as firebase from 'firebase/app';

import { AOAuthService } from './ao-auth.service';
import { AOAuthProviderInterface } from './ao-auth-provider.interface';
import { AOFirebaseService } from './ao-firebase.service';
import { AOSubscriptions } from '../utils/ao-subscriptions';
import { AOEnvService } from './ao-env.service';

@Injectable({
  providedIn: 'root'
})
export class AOAuthFirebaseService implements AOAuthProviderInterface {

  private _subscriptions: AOSubscriptions | any = new AOSubscriptions();

  constructor(
    private _auth: AOAuthService,
    private _fb: AOFirebaseService,
    @Inject(AOEnvService) private _env
  ) {
    registerWebPlugin(OAuth2Client);

    this._auth.provider(this);
    this._auth.isUser() ? this.authListening() : null;
  }

  // -------------------------------------------------------------------------------------------------------------------

  login(data: any): Promise<any> {
    this._subscriptions.stop();

    const promise = new Promise(async (resolve, reject) => {
      try {
        let oauth = null;
        let credential = null;

        if (data.provider instanceof firebase.auth.GoogleAuthProvider) {
          oauth = await this.loginGoogle();
          credential = firebase.auth.GoogleAuthProvider.credential(null, oauth.access_token);

        } else if (data.provider instanceof firebase.auth.FacebookAuthProvider) {
          oauth = await this.loginFacebook();
          credential = firebase.auth.FacebookAuthProvider.credential(oauth.access_token);

        } else if (data.provider instanceof firebase.auth.TwitterAuthProvider) {
          oauth = await this.loginTwitter();
          credential = firebase.auth.TwitterAuthProvider.credential(oauth.access_token, '');

        } else if (data.provider instanceof firebase.auth.GithubAuthProvider) {
          oauth = await this.loginGithub();
          credential = firebase.auth.GithubAuthProvider.credential(oauth.access_token);

        } else {
          reject('Invalid Auth Provider.');
        }

        const auth = await this._fb.auth().auth.signInWithCredential(credential);

        const user = await this.userCreateOrUpdate(auth.user, {
          given_name: oauth.given_name,
          family_name: oauth.family_name,
          locale: oauth.locale,
        });

        resolve(user);
      } catch (e) {
        reject(e);
      }

    });

    promise.finally(() => this.authListening(true));

    return promise;
  }

  logout(): Promise<any> {
    this._subscriptions.stop();
    return this._fb.firebase().destroy().then(() => this._fb.conn().logout());
  }

  update(data: any): Promise<any> {
    return Promise.resolve();
  }

  forgetPassword(data: any): Promise<any> {
    return Promise.resolve();
  }

  resetPassword(data: any): Promise<any> {
    return Promise.resolve();
  }

  updatePassword(data: any): Promise<any> {
    return Promise.resolve();
  }

  // -------------------------------------------------------------------------------------------------------------------

  private loginGoogle(): OAuth2AuthenticateOptions {
    return Plugins.OAuth2Client.authenticate({
      scope: 'email profile',
      authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/auth',
      resourceUrl: 'https://www.googleapis.com/userinfo/v2/me',
      accessTokenEndpoint: 'https://www.googleapis.com/oauth2/v4/token',
      web: {
        appId: this._env.oauth.google.web,
        responseType: 'token',
        redirectUrl: this._env.app_url,
        accessTokenEndpoint: '',
        pkceEnable: false,
        windowOptions: 'height=500,left=0,top=0',
      },
      android: {
        appId: this._env.oauth.google.android,
        responseType: 'code', // if you configured a android app in google dev console the value must be "code"
        redirectUrl: this._env.app_id + ':/' // package name from google dev console
      },
      ios: {
        appId: this._env.oauth.google.ios,
        responseType: 'code', // if you configured a ios app in google dev console the value must be "code"
        redirectUrl: this._env.app_id + ':/' // Bundle ID from google dev console
      }
    });
  }

  private loginFacebook(): OAuth2AuthenticateOptions {
    return this.loginGoogle();
  }

  private loginTwitter(): OAuth2AuthenticateOptions {
    return this.loginGoogle();
  }

  private loginGithub(): OAuth2AuthenticateOptions {
    return this.loginGoogle();
  }

  private authListening(skip = false) {
    this._subscriptions.stop();

    this._subscriptions.auth = this._fb.authState().subscribe(async (auth) => {
      if (auth) {
        if (skip === false) {
          skip = false;
          await this.userCreateOrUpdate(auth);
        }

        this._subscriptions.user = this._fb.fsDocSnapshot('/users/' + auth.uid).subscribe(snapshot => {

          return snapshot.payload.exists
            ? this._auth.setUser(snapshot.payload.data())
            : this._auth.logout();

        }, () => {
          console.warn('Oops! Failed to watch user.');
        });

      } else {
        return this._auth.logout();
      }
    }, () => {
      console.warn('Oops! Failed to update authentication.');
    });
  }

  private async userCreateOrUpdate(user: User, extra = {}) {
    const userRef = this._fb.fsDoc('/users/' + user.uid);
    const userDoc = await userRef.ref.get();

    const data: any = {
      ...userDoc.data(),
      id: user.uid,
      email: user.email,
      email_verified: user.emailVerified,
      name: user.displayName,
      image: user.photoURL,
      logged_at: this._fb.fsNow(),
      ...extra
    };

    if (!userDoc.exists) {
      data.active = true;
      data.created_at = this._fb.fsNow();
      data.updated_at = this._fb.fsNow();
      data.deleted_at = null;
    }

    await userRef.set(data, {merge: true});

    return (await userRef.ref.get()).data();
  }

  // refreshToken: string;

  // onOAuthRefreshBtnClick() {
  //   if (!this.refreshToken) {
  //     console.error('No refresh token found. Log in with OAuth first.');
  //   }
  //   Plugins.OAuth2Client.refreshToken(
  //     {} as OAuth2RefreshTokenOptions
  //   ).then((response: any) => {
  //     console.log('onOAuthRefreshBtnClick', response);
  //   }).catch(error => {
  //     console.error('onOAuthRefreshBtnClick:error', error);
  //   });
  // }

  // onLogoutClick() {
  //   Plugins.OAuth2Client.logout(
  //     {} as OAuth2AuthenticateOptions
  //   ).then((response: any) => {
  //     console.log('onLogoutClick', response);
  //   }).catch(error => {
  //     console.error('onLogoutClick:error', error);
  //   });
  // }

}
